import { createAction, props } from '@ngrx/store';
import { UserStoreEntriesState, UserStorePersistedState } from '../user-store.state';

/** Dispatched to (re)load the user-store mirror from the backend. */
export const loadUserStore = createAction('[UserStore] Load');

/** Dispatched by the load effect once the bulk backend GET succeeds. Triggers a persist of the slice. */
export const userStoreLoaded = createAction('[UserStore] Loaded', props<{ entries: UserStoreEntriesState }>());

/**
 * Dispatched by the persistence bridge with the entries AND pending deletes read back from
 * storage (see {@link UserStorePersistedState}). Populates the slice like {@link userStoreLoaded}
 * but must NOT mark the backend as settled — the data came from storage, not the network (see
 * `hasBackendSettled` in `user-store.state.ts`).
 */
export const userStoreLoadedFromStorage = createAction(
    '[UserStore] Loaded from storage',
    props<UserStorePersistedState>(),
);

/** Dispatched by the load effect when the bulk backend GET fails. */
export const userStoreFailed = createAction('[UserStore] Load failed');

/**
 * Dispatched by a caller to write `value` under `key`, both into the mirror and through to Azure.
 * Not handled by the reducer directly — see {@link userStoreEntryMirrored} for why.
 */
export const setUserStoreValue = createAction('[UserStore] Set value', props<{ key: string; value: unknown }>());

/**
 * Dispatched by `UserStoreEffects` once it has confirmed `key` is not in
 * {@link USER_STORE_UNMIRRORED_KEYS}. This — not {@link setUserStoreValue} — is what the reducer
 * handles, so an excluded key's value is never written into the slice even though the PUT to
 * Azure still goes out unconditionally.
 */
export const userStoreEntryMirrored = createAction(
    '[UserStore] Entry mirrored',
    props<{ key: string; value: unknown }>(),
);

/**
 * Dispatched once `setUserStoreValue`'s PUT to Azure has been accepted. Despite the name, this is
 * NOT a server acknowledgement that `key` is durably stored: `ENDPOINTS.AzUserStore` is registered
 * with `PaceOfflineModule`, so `OfflineInterceptorService` queues the PUT — even while online, to
 * survive a connectivity loss mid-request — and answers immediately with a synthetic `{ d: {} }`
 * before the real backend has necessarily seen it (see `OfflineInterceptorService.savePostRequest`
 * and `UserStoreApiService.putEntry`). Read it as "accepted locally / queued for Azure". The
 * mirror itself is persisted independently, on `userStoreEntryMirrored` (see `UserStoreEffects`),
 * not on this action.
 */
export const setUserStoreValueSuccess = createAction('[UserStore] Set value success', props<{ key: string }>());

/** Dispatched when `setUserStoreValue`'s PUT to Azure fails. The mirror is NOT rolled back — see `UserStoreEffects`. */
export const setUserStoreValueFailed = createAction(
    '[UserStore] Set value failed',
    props<{ key: string; error: string }>(),
);

/** Dispatched by a caller to remove `key`, both from the mirror and from Azure. */
export const deleteUserStoreValue = createAction('[UserStore] Delete value', props<{ key: string }>());

/**
 * Dispatched by `UserStoreEffects` once it has confirmed `key` is not excluded — mirrors {@link
 * userStoreEntryMirrored} for deletes. Also the moment `key` is added to `pendingDeletes` (see
 * `unmirrorUserStoreEntryReducer`): a mirrored key's delete always goes into that retry queue,
 * because the DELETE itself bypasses the offline request queue (see `pendingDeletes`'s doc comment
 * in `user-store.state.ts`) and so has no other way to survive being offline. Triggers a persist
 * of the slice, immediately — the entry's removal and its `pendingDeletes` entry must both survive
 * a restart even before Azure has answered.
 */
export const userStoreEntryUnmirrored = createAction('[UserStore] Entry unmirrored', props<{ key: string }>());

/**
 * Dispatched once `deleteUserStoreValue`'s DELETE against Azure succeeds — including a 404, which
 * `UserStoreApiService.deleteEntry` treats as success since it means `key` is already gone.
 * Clears `key` out of `pendingDeletes` (see `clearPendingUserStoreDeleteReducer`), whether it was
 * a first attempt or a `retryPendingUserStoreDeletes` retry, and triggers a persist of the slice
 * (see `persistUserStoreOnDeleteConfirmed$`) so that cleared `pendingDeletes` entry does not linger
 * in storage until some unrelated change happens to write it out.
 */
export const deleteUserStoreValueSuccess = createAction('[UserStore] Delete value success', props<{ key: string }>());

/** Dispatched when `deleteUserStoreValue`'s DELETE against Azure fails. */
export const deleteUserStoreValueFailed = createAction(
    '[UserStore] Delete value failed',
    props<{ key: string; error: string }>(),
);

/**
 * Dispatched by `UserStoreEffects` once the device is back online and the offline request queue
 * has drained, to resend the DELETE for every key still in `pendingDeletes`. See
 * `UserStoreEffects.triggerPendingUserStoreDeleteRetry$` for why both conditions matter, and its
 * class doc for the ordering caveat this cannot fully close.
 */
export const retryPendingUserStoreDeletes = createAction('[UserStore] Retry pending deletes');
